import React, { useCallback, useEffect, useState } from "react";
import { getToken } from '../utils/cookieUtils';
import { Box, Dialog, Typography } from "@mui/material";
import toast from "react-hot-toast";
import { apiCall, getJsonResponse } from '../utils/apiUtils';

const CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
const CLIENT_SECRET = process.env.REACT_APP_PAYPAL_SECRET_KEY;
const PARTNER_ID = process.env.REACT_APP_PAYPAL_PARTNER_ID ;
const PAYPAL_API = "https://api-m.paypal.com";
const API_BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Strips all HTML tags from a string and removes any bare URLs left behind by <a> tags.
 */
const stripHtml = (html) =>
  html
    ? html
        .replace(/<a\b[^>]*>.*?<\/a>/gi, "")
        .replace(/<[^>]*>/g, "")
        .replace(/\s+/g, " ")
        .trim()
    : "";

const getPayPalAccessToken = async () => {
  const basicAuth = btoa(`${CLIENT_ID}:${CLIENT_SECRET}`);
  const res = await fetch(`${PAYPAL_API}/v1/oauth2/token`, {
    method: "POST",
    headers: {
      Authorization: `Basic ${basicAuth}`,
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: "grant_type=client_credentials",
  });
  const data = await res.json();
  return data.access_token;
};

export default function LinkPayment({
  isPaymentLinked,
  paymentAccountDetails,
  syncStatusDetails,
  className,
  style,
  onAddChefAccountSuccess,
  syncLoading = false,
}) {
  const userId = localStorage.getItem("logged_user_id");

  const [loading, setLoading] = useState(false);
  const [showPaymentsReceivableError, setShowPaymentsReceivableError] =
    useState(false);
  const [showEmailConfirmationError, setShowEmailConfirmationError] =
    useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [internalSyncDetails, setInternalSyncDetails] = useState(null);
  const [shouldPollInternal, setShouldPollInternal] = useState(false);

  // Check if all PayPal status conditions are met
  const isPayPalFullyReady = syncStatusDetails?.success === true && 
                            syncStatusDetails.primary_email_confirmed === true && 
                            syncStatusDetails.payments_receivable === true;

  // Popups based on internal sync details first, then fallback to parent syncStatusDetails
  useEffect(() => {
    // If no account exists, no popups
    if (!paymentAccountDetails || paymentAccountDetails.status !== true) {
      setShowPaymentsReceivableError(false);
      setShowEmailConfirmationError(false);
      setShowSuccessDialog(false);
      return;
    }

    // Use internal sync details if available, otherwise use parent syncStatusDetails
    const currentSyncDetails = internalSyncDetails || syncStatusDetails;
    
    if (currentSyncDetails) {
      const {
        primary_email_confirmed,
        payments_receivable,
        message: syncMessage,
      } = currentSyncDetails;

      if (!primary_email_confirmed) {
        setErrorMessage(
          syncMessage ||
            "Please confirm your PayPal email address to continue receiving payments."
        );
        setShowEmailConfirmationError(true);
        setShowPaymentsReceivableError(false);
        setShowSuccessDialog(false);
      } else if (!payments_receivable) {
        setErrorMessage(
          syncMessage ||
            "Your PayPal account cannot receive payments. Please complete your account setup."
        );
        setShowPaymentsReceivableError(true);
        setShowEmailConfirmationError(false);
        setShowSuccessDialog(false);
      } else {
        // Fully ready
        setShowSuccessDialog(true);
        setShowEmailConfirmationError(false);
        setShowPaymentsReceivableError(false);

        const timer = setTimeout(() => setShowSuccessDialog(false), 5000);
        return () => clearTimeout(timer);
      }
    } else {
      // When sync details are not available yet, hide all popups
      setShowPaymentsReceivableError(false);
      setShowEmailConfirmationError(false);
      setShowSuccessDialog(false);
    }
  }, [paymentAccountDetails, syncStatusDetails, internalSyncDetails]);

  const openPayPalInNewTab = () => {
    window.open("https://www.paypal.com", "_blank", "noopener,noreferrer");
  };

  const checkInternalPaymentStatus = useCallback(async () => {
    try {
      const userId = localStorage.getItem("logged_user_id");
      const authToken = getToken();

      // Step 1: Get account details from get_chef_account_details
      const accountRes = await apiCall(
        `${API_BASE_URL}/user/get_chef_account_details`,
        {
          method: "POST",
          body: JSON.stringify({ user_id: userId }),
        }
      );
      const accountData = await getJsonResponse(accountRes);

      // If account does not exist according to get_chef_account_details
      if (accountData.status !== true) {
        setInternalSyncDetails(null);
        setShouldPollInternal(false);
        return;
      }

      // Check if account is fully ready from get_chef_account_details response
      const emailStatus = String(accountData.emailStatus || "")
        .replace(/[\s_]/g, "")
        .toUpperCase();
      
      if (emailStatus === "CONFIRMED" && accountData.payments_receivable === true) {
        // Account is fully ready, no need to call syncChefPayPalStatus
        setInternalSyncDetails({
          success: true,
          primary_email_confirmed: true,
          payments_receivable: true,
          message: accountData.message || "Your PayPal account is fully set up and ready to receive payments."
        });
        setShouldPollInternal(false);
        return;
      }

      // Account exists but not fully ready - get real-time sync status
      const syncRes = await apiCall(
        `${API_BASE_URL}/user/syncChefPayPalStatus`,
        {
          method: "POST",
          body: JSON.stringify({ userId }),
        }
      );
      const syncData = await getJsonResponse(syncRes);
      setInternalSyncDetails(syncData);

      // Determine if polling is needed - continue until all three values are true
      const isFullyReady =
        syncData.success === true &&
        syncData.primary_email_confirmed === true &&
        syncData.payments_receivable === true;
      setShouldPollInternal(!isFullyReady);
    } catch (err) {
      console.error("Internal payment status check failed:", err);
      setInternalSyncDetails(null);
      setShouldPollInternal(false);
    }
  }, []);

  // Polling for internal sync status
  useEffect(() => {
    if (!shouldPollInternal) return;

    const intervalId = setInterval(checkInternalPaymentStatus, 5000);
    return () => clearInterval(intervalId);
  }, [shouldPollInternal, checkInternalPaymentStatus]);

  const saveMerchantIdToBackend = useCallback(
    async (merchantId, email) => {
      try {
        const res = await fetch(`${API_BASE_URL}/user/add_chef_account`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer " + getToken()
          },
          body: JSON.stringify({
            user_id: userId,
            merchantId,
            Email: email,
            merchantIdInPayPal: merchantId,
            permissionsGranted: true,
            accountStatus: "active",
            consentStatus: "active",
            isEmailConfirmed: "true",
            returnMessage: "Onboarding complete",
          }),
        });
        const data = await getJsonResponse(res);

        if (data.status === true) {
          toast.success(data.message || "Something went wrong. Please try again.");
        } else {
          console.error(data.message || "Something went wrong. Please try again.");
        }
        
        // After add_chef_account, immediately call syncChefPayPalStatus regardless of its response
        try {
          const userId = localStorage.getItem("logged_user_id");
          const authToken = getToken();
          
          const syncRes = await apiCall(
            `${API_BASE_URL}/user/syncChefPayPalStatus`,
            {
              method: "POST",
              body: JSON.stringify({ userId }),
            }
          );
          const syncData = await getJsonResponse(syncRes);
          setInternalSyncDetails(syncData);
          
          // Check if all three values are true, if not, start polling
          const isFullyReady =
            syncData.success === true &&
            syncData.primary_email_confirmed === true &&
            syncData.payments_receivable === true;
          setShouldPollInternal(!isFullyReady);
          
          // Then call the full checkInternalPaymentStatus for complete status
          checkInternalPaymentStatus();
        } catch (syncErr) {
          console.error("Immediate sync status check failed:", syncErr);
          // Fallback to regular check
          checkInternalPaymentStatus();
        }
        
        // Also call parent callback to update parent components
        onAddChefAccountSuccess?.();
      } catch (err) {
        console.error("Save merchant error:", err);
        console.error("Failed to save merchant account. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [userId, onAddChefAccountSuccess]
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const merchantId = params.get("merchantIdInPayPal");
    const email = params.get("email");

    if (merchantId) {
      setLoading(true);
      saveMerchantIdToBackend(merchantId, email);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [saveMerchantIdToBackend]);

  // Enhanced mobile detection
  const isMobileDevice = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ||
           window.innerWidth <= 768;
  };

  const isInAppBrowser = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    return /FBAN|FBAV|Instagram|WhatsApp|Twitter|Line|WeChat|LinkedIn|Snapchat/i.test(ua) ||
           window.webkit?.messageHandlers ||
           window.Android ||
           (window.webkit && window.webkit.messageHandlers);
  };

  const handleOnboarding = async () => {
    setLoading(true);
    try {
      const accessToken = await getPayPalAccessToken();
      const currentPath = window.location.pathname.replace(/^\//, "");

      const res = await fetch(`${API_BASE_URL}/user/partner_referral`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: userId,
          partner_id: PARTNER_ID,
          return_url: `${window.location.origin}/link-payment/success?path=${currentPath}`,
          return_url_description: "Return to Chef Republic",
          products: ["PPCP", "ADVANCED_VAULTING"],
          capabilities: ["PAYPAL_WALLET_VAULTING_ADVANCED"],
          partner_config_override: {
            return_url: `${window.location.origin}/${currentPath}`,
            return_url_description: "Back to Chef Republic",
          },
        }),
      });
      const data = await res.json();

      if (data?.signup_link) {
        // Desktop flow - direct redirect (existing working flow)
        if (!isMobileDevice() && !isInAppBrowser()) {
          window.location.href = data.signup_link;
        } 
        // Mobile flow - open in new tab to avoid session issues
        else {
          //toast.loading("Opening PayPal...", { id: "paypal-loading" });
          const paypalWindow = window.open(data.signup_link, '_blank', 'noopener,noreferrer');
          
          // Poll for window closure to refresh status
          const checkClosed = setInterval(() => {
            if (paypalWindow?.closed) {
              clearInterval(checkClosed);
              toast.dismiss("paypal-loading");
              // Refresh to check PayPal status after return
              window.location.reload();
            }
          }, 1000);
          
          // Fallback timeout
          setTimeout(() => {
            clearInterval(checkClosed);
            toast.dismiss("paypal-loading");
          }, 300000); // 5 minutes
        }
      } else {
        toast.error("PayPal onboarding failed. Please try again.");
      }
    } catch (err) {
      console.error("Onboarding error:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <button
        type="button"
        className={className || "btn btn-orange rounded-pill mb-3"}
        disabled={isPayPalFullyReady || loading || syncLoading}
        onClick={handleOnboarding}
        style={{
          boxSizing: "border-box",
          maxWidth: "100%",
          width: "100%",
          backgroundColor: isPayPalFullyReady ? "#cccbcb" : "#FF5E41",
          color: "#fff",
          cursor: isPayPalFullyReady || loading || syncLoading ? "not-allowed" : "pointer",
          ...style,
        }}
      >
        {loading || syncLoading ? (
          <span
            className="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
          />
        ) : (
          "Link Payment Method"
        )}
      </button>

      {/* Dialog 1 – Payments Receivable Issue */}
      <Dialog
        open={showPaymentsReceivableError}
        onClose={() => setShowPaymentsReceivableError(false)}
        PaperProps={{
          sx: { width: "400px", borderRadius: "15px", maxWidth: "90vw" },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Account Setup Required
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ mb: 2, color: "#666" }}>
            {stripHtml(errorMessage)}
          </Typography>

          <a
            href="https://www.paypal.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#4441ff",
              textDecoration: "underline",
              fontSize: "14px",
            }}
          >
            www.paypal.com
          </a>

          <Box sx={{ mt: 3 }}>
            <button
              onClick={openPayPalInNewTab}
              className="btn btn-orange rounded-pill"
              style={{ width: "100%" }}
            >
              Go to PayPal
            </button>
          </Box>
        </Box>
      </Dialog>

      {/* Dialog 2 – Email Confirmation Required */}
      <Dialog
        open={showEmailConfirmationError}
        onClose={() => setShowEmailConfirmationError(false)}
        PaperProps={{
          sx: { width: "400px", borderRadius: "15px", maxWidth: "90vw" },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Email Confirmation Required
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ mb: 3, color: "#666" }}>
            {stripHtml(errorMessage)}
          </Typography>

          <Box>
            <button
              onClick={openPayPalInNewTab}
              className="btn btn-orange rounded-pill"
              style={{ width: "100%" }}
            >
              Confirm Email Address
            </button>
          </Box>
        </Box>
      </Dialog>

      {/* Dialog 3 – Success */}
      {/* <Dialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        PaperProps={{
          sx: { width: "400px", borderRadius: "15px", maxWidth: "90vw" },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box
              sx={{
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                backgroundColor: "#4CAF50",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography sx={{ color: "#fff", fontSize: "30px" }}>✓</Typography>
            </Box>
          </Box>

          <Typography
            variant="h6"
            sx={{ fontWeight: 600, textAlign: "center", mb: 1 }}
          >
            Ready to Receive Payments!
          </Typography>

          <Typography
            variant="body1"
            sx={{ mb: 3, color: "#666", textAlign: "center" }}
          >
            Your PayPal account is fully set up and ready to receive payments from
            customers.
          </Typography>
        </Box>
      </Dialog> */}
    </Box>
  );
}